import axios from 'axios';
import { fetchAuthSession } from 'aws-amplify/auth';

const LAB_API_URL = process.env.PATHOLOGY_API_URL;

class LabAPIService {

    // ─── Auth ────────────────────────────────────────────────────────────────────

    async getHeaders() {
        const res = await fetchAuthSession();
        return {
            headers: {
                'Content-Type': 'application/json',
                authorizationToken: `${res.tokens.idToken}`,
            },
        };
    }

    // ─── FOLDER 1: Catalog & Reference Configuration (16 methods) ────────────────

    /** 1.1 Create Lab Test */
    async createLabTest(data: {
        testName: string;
        testCode: string;
        category: string;
        price: number;
        cgstPct?: number;
        sgstPct?: number;
        igstPct?: number;
        sampleType?: string;
        turnaroundHours?: number;
        unit?: string;
        description?: string;
    }) {
        const header = await this.getHeaders();
        return axios.post(`${LAB_API_URL}lab/tests`, data, header).then(res => res.data);
    }

    /** 1.2 List Lab Tests */
    async getLabTests(params?: { category?: string; search?: string }) {
        const header = await this.getHeaders();
        const query = new URLSearchParams();
        if (params?.category) query.append('category', params.category);
        if (params?.search) query.append('search', params.search);
        const qs = query.toString() ? `?${query.toString()}` : '';
        return axios.get(`${LAB_API_URL}lab/tests${qs}`, header).then(res => res.data);
    }

    /** 1.3 Get Lab Test Details */
    async getLabTest(testId: string) {
        const header = await this.getHeaders();
        return axios.get(`${LAB_API_URL}lab/tests/${encodeURIComponent(testId)}`, header).then(res => res.data);
    }

    /** 1.4 Update Lab Test */
    async updateLabTest(testId: string, data: Partial<{
        testName: string;
        price: number;
        category: string;
        turnaroundHours: number;
        unit: string;
        description: string;
        sampleType: string;
        cgstPct: number;
        sgstPct: number;
        igstPct: number;
    }>) {
        const header = await this.getHeaders();
        return axios.put(`${LAB_API_URL}lab/tests/${encodeURIComponent(testId)}`, data, header).then(res => res.data);
    }

    /** 1.5 Delete Lab Test */
    async deleteLabTest(testId: string) {
        const header = await this.getHeaders();
        return axios.delete(`${LAB_API_URL}lab/tests/${encodeURIComponent(testId)}`, header).then(res => res.data);
    }

    /** 1.6 Save Test Parameters (Reference Ranges) */
    async saveTestParameters(testId: string, parameters: Array<{
        paramName: string;
        unit: string;
        referenceRange: string;
        gender?: string;
        ageRange?: string;
        criticalLow?: string;
        criticalHigh?: string;
    }>) {
        const header = await this.getHeaders();
        return axios
            .put(`${LAB_API_URL}lab/tests/${encodeURIComponent(testId)}/parameters`, { parameters }, header)
            .then(res => res.data);
    }

    /** 1.7 Get Test Parameters Template */
    async getTestParameters(testId: string) {
        const header = await this.getHeaders();
        return axios
            .get(`${LAB_API_URL}lab/tests/${encodeURIComponent(testId)}/parameters`, header)
            .then(res => res.data);
    }

    /** 1.8 Create Test Group (Panel Profiles) */
    async createTestGroup(data: {
        groupName: string;
        groupCode?: string;
        description?: string;
        testIds?: string[];
        tests?: string[];
        price?: number;
    }) {
        const header = await this.getHeaders();
        const { tests, ...rest } = data;
        const payload = { ...rest, testIds: rest.testIds ?? tests ?? [] };
        return axios.post(`${LAB_API_URL}lab/test-groups`, payload, header).then(res => res.data);
    }

    /** 1.9 Get Test Groups */
    async getTestGroups() {
        const header = await this.getHeaders();
        return axios.get(`${LAB_API_URL}lab/test-groups`, header).then(res => res.data);
    }

    /** 1.10 Create Reference Doctor */
    async createReferenceDoctor(data: {
        doctorName: string;
        specialization?: string;
        mobile?: string;
        email?: string;
        hospital?: string;
        commissionPct?: number;
    }) {
        const header = await this.getHeaders();
        return axios.post(`${LAB_API_URL}lab/reference-doctors`, data, header).then(res => res.data);
    }

    /** 1.11 List Reference Doctors */
    async listReferenceDoctors() {
        const header = await this.getHeaders();
        return axios.get(`${LAB_API_URL}lab/reference-doctors`, header).then(res => res.data);
    }

    /** 1.12 Update Reference Doctor */
    async updateReferenceDoctor(doctorId: string, data: Partial<{
        doctorName: string;
        specialization: string;
        mobile: string;
        email: string;
        hospital: string;
        commissionPct: number;
    }>) {
        const header = await this.getHeaders();
        return axios
            .put(`${LAB_API_URL}lab/reference-doctors/${encodeURIComponent(doctorId)}`, data, header)
            .then(res => res.data);
    }

    /** 1.13 Create Sample Type Configuration */
    async createSampleType(data: {
        sampleTypeName: string;
        bottleColor?: string;
        department?: string;
        handlingInstructions?: string;
    }) {
        const header = await this.getHeaders();
        return axios.post(`${LAB_API_URL}lab/sample-types`, data, header).then(res => res.data);
    }

    /** 1.14 List Sample Types */
    async listSampleTypes() {
        const header = await this.getHeaders();
        return axios.get(`${LAB_API_URL}lab/sample-types`, header).then(res => res.data);
    }

    /** 1.15 Get Lab Settings Config */
    async getLabConfig() {
        const header = await this.getHeaders();
        return axios.get(`${LAB_API_URL}lab/config`, header).then(res => res.data);
    }

    /** 1.16 Update Lab Settings Config */
    async updateLabConfig(data: {
        configType: string;
        configId: string;
        labName?: string;
        address?: string;
        contact?: string;
        email?: string;
        [key: string]: any;
    }) {
        const header = await this.getHeaders();
        return axios.put(`${LAB_API_URL}lab/config`, data, header).then(res => res.data);
    }

    // ─── FOLDER 2: Billing & Order Creation (9 methods) ──────────────────────────

    /** 2.1 Place Lab Order */
    async placeOrder(data: {
        patientId: string;
        patientName: string;
        mobile?: string;
        email?: string;
        age?: number;
        gender?: string;
        address?: string;
        referenceDoctor?: string;
        discountAmount?: number;
        paidAmount?: number;
        paymentMode?: string;
        comments?: string;
        /** Triage priority — 'stat' | 'urgent' | 'routine' */
        priority?: string;
        tests: Array<{ testId: string; quantity?: number; discountPct?: number }>;
    }) {
        const header = await this.getHeaders();
        return axios.post(`${LAB_API_URL}lab/orders`, data, header).then(res => res.data);
    }

    /** 2.2 List Lab Orders */
    async listOrders(params?: {
        startDate?: string;
        endDate?: string;
        status?: string;
        mobile?: string;
        limit?: number;
        next_token?: string;
    }) {
        const header = await this.getHeaders();
        const query = new URLSearchParams();
        if (params?.startDate) query.append('startDate', params.startDate);
        if (params?.endDate) query.append('endDate', params.endDate);
        if (params?.status) query.append('status', params.status);
        if (params?.mobile) query.append('mobile', params.mobile);
        if (params?.limit) query.append('limit', String(params.limit));
        if (params?.next_token) query.append('next_token', params.next_token);
        const qs = query.toString() ? `?${query.toString()}` : '';
        return axios.get(`${LAB_API_URL}lab/orders${qs}`, header).then(res => res.data);
    }

    /** 2.3 Get Lab Order Details */
    async getOrder(orderId: string) {
        const header = await this.getHeaders();
        return axios.get(`${LAB_API_URL}lab/orders/${encodeURIComponent(orderId)}`, header).then(res => res.data);
    }

    /** 2.4 Update Lab Order Details */
    async updateOrder(orderId: string, data: { orderStatus?: string; comments?: string;[key: string]: any }) {
        const header = await this.getHeaders();
        return axios
            .put(`${LAB_API_URL}lab/orders/${encodeURIComponent(orderId)}`, data, header)
            .then(res => res.data);
    }

    /** 2.5 Cancel Lab Order */
    async cancelOrder(orderId: string) {
        const header = await this.getHeaders();
        return axios.delete(`${LAB_API_URL}lab/orders/${encodeURIComponent(orderId)}`, header).then(res => res.data);
    }

    /** 2.6 Search Order by Invoice Number */
    async getOrderByInvoice(invoiceNumber: string) {
        const header = await this.getHeaders();
        return axios
            .get(`${LAB_API_URL}lab/orders/invoice/${encodeURIComponent(invoiceNumber)}`, header)
            .then(res => res.data);
    }

    /** 2.7 Generate Invoice PDF */
    async generateInvoicePdf(orderId: string) {
        const header = await this.getHeaders();
        return axios
            .post(`${LAB_API_URL}lab/orders/${encodeURIComponent(orderId)}/invoice/pdf`, {}, header)
            .then(res => res.data);
    }

    /** 2.8 Record Payment (Clear Balance) */
    async recordPayment(orderId: string, data: {
        amount: number;
        paymentMode: string;
        transactionId?: string;
        comments?: string;
    }) {
        const header = await this.getHeaders();
        return axios
            .post(`${LAB_API_URL}lab/orders/${encodeURIComponent(orderId)}/payment`, data, header)
            .then(res => res.data);
    }

    /** 2.9 Fetch Dashboard Analytics Stats */
    async getDashboardStats() {
        const header = await this.getHeaders();
        return axios.get(`${LAB_API_URL}lab/dashboard`, header).then(res => res.data);
    }

    // ─── FOLDER 3: Sample Collection Logistics (5 methods) ───────────────────────

    /** 3.1 Register Specimen Vial */
    async registerSample(orderId: string, data: {
        testId: string;
        sampleTypeName?: string;
        bottleType?: string;
        collectedMaxQty?: string;
        collectedMinQty?: string;
        collectedBy?: string;
    }) {
        const header = await this.getHeaders();
        return axios
            .post(`${LAB_API_URL}lab/orders/${encodeURIComponent(orderId)}/samples`, data, header)
            .then(res => res.data);
    }

    /** 3.2 Get Samples for Order */
    async getSamplesForOrder(orderId: string) {
        const header = await this.getHeaders();
        return axios
            .get(`${LAB_API_URL}lab/orders/${encodeURIComponent(orderId)}/samples`, header)
            .then(res => res.data);
    }

    /** 3.3 Update Specimen Status (e.g. Collected / Received) */
    async updateSample(sampleId: string, data: { isCollected?: string; comments?: string;[key: string]: any }) {
        const header = await this.getHeaders();
        return axios
            .put(`${LAB_API_URL}lab/samples/${encodeURIComponent(sampleId)}`, data, header)
            .then(res => res.data);
    }

    /** 3.4 Delete Specimen Record */
    async deleteSample(sampleId: string) {
        const header = await this.getHeaders();
        return axios.delete(`${LAB_API_URL}lab/samples/${encodeURIComponent(sampleId)}`, header).then(res => res.data);
    }

    /** 3.5 Get Sample Barcode (SVG) */
    async getSampleBarcode(sampleId: string) {
        const header = await this.getHeaders();
        return axios
            .get(`${LAB_API_URL}lab/samples/${encodeURIComponent(sampleId)}/barcode`, header)
            .then(res => res.data);
    }

    // ─── FOLDER 4: Test Processing & Medical Validation (7 methods) ──────────────

    /** 4.1 Save Draft Test Results */
    async saveDraftResult(orderId: string, data: {
        testId: string;
        testName?: string;
        findings?: string;
        impression?: string;
        parameters?: Array<{ paramName: string; value: string; unit?: string; referenceRange?: string; criticalLow?: string; criticalHigh?: string }>;
        /** Identity of the technician who entered this draft — used by the client-side Verify checkpoint */
        enteredBy?: string;
        verifiedBy?: string;
        verifiedAt?: string;
        criticalAcknowledgedBy?: string;
        criticalAcknowledgedAt?: string;
        criticalNote?: string;
    }) {
        const header = await this.getHeaders();
        return axios
            .post(`${LAB_API_URL}lab/orders/${encodeURIComponent(orderId)}/results`, data, header)
            .then(res => res.data);
    }

    /** 4.2 Get Order Results */
    async getOrderResults(orderId: string) {
        const header = await this.getHeaders();
        return axios
            .get(`${LAB_API_URL}lab/orders/${encodeURIComponent(orderId)}/results`, header)
            .then(res => res.data);
    }

    /** 4.3 Get Single Test Result Details */
    async getResult(orderId: string, resultId: string) {
        const header = await this.getHeaders();
        return axios
            .get(`${LAB_API_URL}lab/orders/${encodeURIComponent(orderId)}/results/${encodeURIComponent(resultId)}`, header)
            .then(res => res.data);
    }

    /** 4.4 Update Test Result */
    async updateResult(resultId: string, data: {
        findings?: string;
        impression?: string;
        parameters?: Array<{ paramName: string; value: string; unit?: string; referenceRange?: string; criticalLow?: string; criticalHigh?: string }>;
        /** Identity of the technician who entered this draft — used by the client-side Verify checkpoint */
        enteredBy?: string;
        verifiedBy?: string;
        verifiedAt?: string;
        criticalAcknowledgedBy?: string;
        criticalAcknowledgedAt?: string;
        criticalNote?: string;
    }) {
        const header = await this.getHeaders();
        return axios.put(`${LAB_API_URL}lab/results/${encodeURIComponent(resultId)}`, data, header).then(res => res.data);
    }

    /** 4.5 Submit for Medical Review */
    async submitForReview(resultId: string) {
        const header = await this.getHeaders();
        return axios
            .post(`${LAB_API_URL}lab/results/${encodeURIComponent(resultId)}/submit`, {}, header)
            .then(res => res.data);
    }

    /** 4.6 Pathologist Approve Report */
    async approveReport(resultId: string) {
        const header = await this.getHeaders();
        return axios
            .post(`${LAB_API_URL}lab/results/${encodeURIComponent(resultId)}/approve`, {}, header)
            .then(res => res.data);
    }

    /** 4.7 Pathologist Reject Report */
    async rejectReport(resultId: string, reason: string) {
        const header = await this.getHeaders();
        return axios
            .post(`${LAB_API_URL}lab/results/${encodeURIComponent(resultId)}/reject`, { reason }, header)
            .then(res => res.data);
    }

    // ─── FOLDER 5: PDF Synthesis, Dispatch & Delivery (3 methods) ────────────────

    /** 5.1 Generate Lab Report PDF */
    async generateReportPdf(orderId: string) {
        const header = await this.getHeaders();
        return axios
            .post(`${LAB_API_URL}lab/orders/${encodeURIComponent(orderId)}/report/pdf`, {}, header)
            .then(res => res.data);
    }

    /** 5.2 Get Lab Report PDF URL */
    async getReportPdfUrl(orderId: string) {
        const header = await this.getHeaders();
        return axios
            .get(`${LAB_API_URL}lab/orders/${encodeURIComponent(orderId)}/report/pdf`, header)
            .then(res => res.data);
    }

    /** 5.3 Email Lab Report (SES Integration) */
    async emailReport(orderId: string, email: string) {
        const header = await this.getHeaders();
        return axios
            .post(`${LAB_API_URL}lab/orders/${encodeURIComponent(orderId)}/report/email`, { email }, header)
            .then(res => res.data);
    }

    async getSlideLibrary(params?: {
        status?: string;
        patientId?: string;
        date?: string;
        limit?: number;
    }) {
        const header = await this.getHeaders();
        const query = new URLSearchParams();
        if (params?.status) query.append('status', params.status);
        if (params?.patientId) query.append('patientId', params.patientId);
        if (params?.date) query.append('date', params.date);
        if (params?.limit) query.append('limit', String(params.limit));
        const qs = query.toString() ? `?${query.toString()}` : '';
        return axios.get(`${LAB_API_URL}lab/slide-library${qs}`, header).then(res => res.data);
    }

    async updateOrderStatus(orderId: string, slideLibraryStatus: 'WAITING_FOR_LAB_REPORT' | 'SAMPLE_COLLECTED' | 'REPORT_READY') {
        const header = await this.getHeaders();
        return axios
            .patch(`${LAB_API_URL}lab/orders/${encodeURIComponent(orderId)}/status`, { slideLibraryStatus }, header)
            .then(res => res.data);
    }
}

const LabServices = new LabAPIService();
export default LabServices;
